import { Box, Button, Divider, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import CommonCard from "../../common/components/CommonCard";
import theme from "../../theme";
import { Certificate, DisplayGrade, DocumentType, StudentGrade, UserTypeId } from "../../common/const";
import CommonButton from "../../common/components/CommonButton";
import { Add, BorderColor, Check, Close, DeleteForever, Description } from "@mui/icons-material";
import styled from "@emotion/styled";
import moment from "moment";
import CommonDialog from "../../common/components/CommonDialog";
import Dropzone from "react-dropzone";
import CommonSelect from "../../common/components/CommonSelect";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import CommonFormField from "../../common/components/CommonFormField";
import { createDocument, deleteDocument, getDocuments, updateDocument } from "../../api/documentApi";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const Certificates = () => {
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [testType, setTestType] = useState(Certificate.Transcripts);
    const [addOpen, setAddOpen] = useState(false);
    const [fileNames, setFileNames] = useState([]);
    const [grade, setGrade] = useState(StudentGrade[0]?.value);
    const [errors, setErrors] = useState({});
    const [transcriptData, setTranscriptData] = useState(null);
    const [certificateData, setCertificateData] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        issue_date: '',
        valid_date: '',
        notes: '',
    });
    const [documentId, setDocumentId] = useState(null);

    const handleDrop = acceptedFiles => {
        setFileNames(acceptedFiles?.map(files => files));
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                if (!value) return 'Title is required';
                return '';
            case 'issue_date':
                if (!value) return 'Issue Date is required';
                return '';
            case 'valid_date':
                if (!value) return 'Valid Till is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            if (fileNames.length > 0 || fileNames?.url) {
                dispatch(setLoading({
                    loading: true
                }));
                const data = new FormData();
                data.append('type', DocumentType.Certificates);
                data.append('grade', grade);
                data.append('title', formData.name);
                if (!fileNames?.filename) {
                    data.append('documents', fileNames[0]);
                }
                data.append('description', formData.notes);
                data.append('issue_date', new Date(formData.issue_date).toISOString());
                data.append('valid_till', new Date(formData.valid_date).toISOString());
                let response;
                if (documentId) {
                    response = await updateDocument(documentId, data);
                } else {
                    response = await createDocument(data);
                }
                if (response.success) {
                    showNotification(response.message, 'success');
                    setAddOpen(false);
                    setGrade(StudentGrade[0]?.value);
                    setFormData({
                        name: '',
                        issue_date: '',
                        valid_date: '',
                        notes: '',
                    });
                    setDocumentId(null);
                    setFileNames([]);
                    getDocumentData();
                }
                dispatch(setLoading({
                    loading: false
                }));
            } else {
                showNotification('Please select document', 'error');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getDocumentData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getDocuments(testType === Certificate.Transcripts ? DocumentType.Transcripts : DocumentType.Certificates, pageNo + 1, pageSize);
            if (testType === Certificate.Transcripts) {
                setTranscriptData(response?.data);
            } else {
                setCertificateData(response?.data);
            }
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getDocumentData();
    }, [pageNo, pageSize, testType]);


    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const handleDelete = async (id) => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await deleteDocument(id);
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setDeleteConfirm(null);
                getDocumentData();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getMinDate = () => {
        const date = formData.issue_date ? new Date(formData.issue_date) : new Date();
        date.setDate(date.getDate() + 1); // Add 7 days
        return date.toISOString().split('T')[0];
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={!!deleteConfirm}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to delete?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                handleDelete(deleteConfirm.student_document_id)
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteConfirm(null)
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonDialog
                open={addOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{testType === Certificate.Transcripts ? `${documentId ? 'Edit' : 'Add'} Transcript` : `${documentId ? 'Edit' : 'Add'} Certificate`}</Typography>
                            </Box>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setFileNames([]);
                                    setFormData({
                                        name: '',
                                        issue_date: '',
                                        valid_date: '',
                                        notes: '',
                                    });
                                    setGrade(StudentGrade[0]?.value);
                                    setDocumentId(null);
                                    setAddOpen(false);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container mt={1} mb={2} spacing={2}>
                        {testType === Certificate.Certificates ? (
                            <Grid item md={12}>
                                <CommonFormField
                                    label="Certificate Title"
                                    type="email"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    error={errors.name}
                                    placeholder="Enter Certificate Title"
                                />
                            </Grid>
                        ) : (
                            <Grid item xs={12} md={12}>
                                <CommonSelect
                                    label="Select Grade"
                                    name="grade"
                                    value={grade}
                                    onChange={(e) => setGrade(e.target.value)}
                                    placeholder="Select Grade"
                                    options={StudentGrade}
                                />
                            </Grid>
                        )}
                        {testType === Certificate.Certificates && (
                            <>
                                <Grid item md={6}>
                                    <CommonFormField
                                        label="Issue Date"
                                        type="date"
                                        name="issue_date"
                                        value={formData.issue_date}
                                        onChange={handleInputChange}
                                        error={errors.issue_date}
                                        placeholder="Select Issue Date"
                                    />
                                </Grid>
                                <Grid item md={6}>
                                    <CommonFormField
                                        label="Valid Till"
                                        type="date"
                                        name="valid_date"
                                        value={formData.valid_date}
                                        onChange={handleInputChange}
                                        error={errors.valid_date}
                                        placeholder="Select Valid Till"
                                        minDate={getMinDate()}
                                    />
                                </Grid>
                                <Grid item md={12}>
                                    <CommonFormField
                                        label="Description"
                                        type="email"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        placeholder="Enter details of certificate"
                                        multiline
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item sm={12} md={12}>
                            <Box sx={{ width: '100%' }}>
                                {fileNames.length > 0 ? (
                                    <Box sx={{ mt: 2 }}>
                                        {fileNames?.map((file, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    backgroundColor: 'background.paper'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Description color="primary" />
                                                    <Box>
                                                        <Typography variant="body1">{file.name || file?.filename}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(file.size / 1024).toFixed(2)} KB
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setFileNames(fileNames.splice(index, -1))}
                                                    color="error"
                                                >
                                                    <Close />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : fileNames.filename ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 2,
                                            border: '1px solid',
                                            borderColor: 'grey.300',
                                            borderRadius: 1,
                                            mb: 1,
                                            backgroundColor: 'background.paper'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Description color="primary" />
                                            <Box>
                                                <Typography variant="body1">{fileNames?.filename}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {(fileNames.size / 1024).toFixed(2)} KB
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => setFileNames([])}
                                            color="error"
                                        >
                                            <Close />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    // Show dropzone when no files
                                    <Dropzone
                                        onDrop={handleDrop}
                                        multiple={false}
                                       accept={{ 'application/pdf': ['.pdf'] }}
                                    >
                                        {({ getRootProps, getInputProps, isDragActive }) => (
                                            <Box
                                                {...getRootProps()}
                                                sx={{
                                                    border: '2px dashed',
                                                    borderColor: '#D8DAEB',
                                                    borderRadius: 2,
                                                    padding: { xs: 3, md: 6 },
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    minHeight: 100,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <input {...getInputProps()} />
                                                <img src="/Images/sound.png" alt="upload" />
                                                <Typography fontWeight={600} gutterBottom>
                                                    Drag and drop your files
                                                </Typography>
                                                <CommonButton
                                                    label="Select File"
                                                    color="Black"
                                                    background="White"
                                                    border={`1px solid ${theme.palette.background.secondary}`}
                                                    width="120px"
                                                    height="40px"
                                                    marginBottom="0px"
                                                    marginTop="10px"
                                                />
                                            </Box>
                                        )}
                                    </Dropzone>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Cancel"
                            size="small"
                            background='white'
                            color={theme.palette.background.dark}
                            border={`1px solid ${theme.palette.background.secondary}`}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={async () => {
                                setAddOpen(false);
                                setFormData({
                                    name: '',
                                    issue_date: '',
                                    valid_date: '',
                                    notes: '',
                                });
                                setGrade(StudentGrade[0]?.value);
                                setFileNames([]);
                                setDocumentId(null);
                            }}
                        />
                        <CommonButton
                            label="Save"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={async () => {
                                if (testType === Certificate.Transcripts) {
                                    if ((fileNames.length > 0 || fileNames.url)) {
                                        try {
                                            dispatch(setLoading({
                                                loading: true
                                            }));
                                            const formData = new FormData();
                                            formData.append('type', DocumentType.Transcripts);
                                            formData.append('grade', grade);
                                            formData.append('title', grade);
                                            if (!fileNames?.filename) {
                                                formData.append('documents', fileNames[0]);
                                            }
                                            let response;
                                            if (documentId) {
                                                response = await updateDocument(documentId, formData);
                                            } else {
                                                response = await createDocument(formData);
                                            }
                                            if (response.success) {
                                                showNotification(response.message, 'success');
                                                setAddOpen(false);
                                                setGrade(StudentGrade[0]?.value);
                                                setFileNames([]);
                                                setDocumentId(null);
                                                getDocumentData();
                                            }
                                            dispatch(setLoading({
                                                loading: false
                                            }));
                                        } catch (err) {
                                            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                            showNotification(errorMessage, 'error');
                                            dispatch(setLoading({
                                                loading: false
                                            }));
                                        }
                                    } else {
                                        showNotification('Please select document', 'error');
                                    }
                                } else {
                                    handleSubmit();
                                }
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonCard
                borderRadius={1}
                border={`1px solid ${theme.palette.background.secondary}`}
                paddingBottom="0px"
                padding="0px"
                marginTop="0px"
                headerContent={
                    <Box>
                        <Box
                            p={2}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box>
                                <Typography fontSize={22} fontWeight={600}>My Transcripts/Certificates</Typography>
                                <Typography fontSize={22} fontWeight={600}>Are you ready to upload?</Typography>
                                <Typography fontSize={15} color={theme.palette.background.dark}>Manage and access your official academic records.</Typography>
                                <CommonCard
                                    padding="10px"
                                    paddingBottom="0px"
                                    background="#FAFAFA"
                                    content={
                                        <Fragment>
                                            <Typography fontSize={14} color={theme.palette.background.dark}><span style={{ fontWeight: 600 }}>Note:</span> Your transcripts are encrypted and stored in our secure storage.</Typography>
                                        </Fragment>
                                    }
                                />
                            </Box>
                            {user?.user_type_id === UserTypeId.Student && (
                                <CommonButton
                                    label={`Add ${testType}`}
                                    width="170px"
                                    marginBottom="0px"
                                    marginTop="0px"
                                    fontWeight={600}
                                    startIcon={<Add />}
                                    handleClick={() => setAddOpen(true)}
                                />)}
                        </Box>
                        <Divider />
                    </Box>
                }
                content={
                    <Fragment>
                        <Grid container spacing={2} sx={{ px: '13px', pb: '16px' }}>
                            <Grid item md={12}>
                                <Box mt={2} display="flex" gap={1}>
                                    <CommonButton
                                        label={Certificate.Transcripts}
                                        width="100px"
                                        fontWeight={600}
                                        color={testType === Certificate.Transcripts ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={testType === Certificate.Transcripts ? theme.palette.primary.main : '#FAFAFA'}
                                        handleClick={() => setTestType(Certificate.Transcripts)}
                                        marginBottom="0px"
                                        marginTop="0px"
                                        height="35px"
                                    />
                                    <CommonButton
                                        label={Certificate.Certificates}
                                        width="100px"
                                        fontWeight={600}
                                        color={testType === Certificate.Certificates ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={testType === Certificate.Certificates ? theme.palette.primary.main : '#FAFAFA'}
                                        handleClick={() => setTestType(Certificate.Certificates)}
                                        marginBottom="0px"
                                        marginTop="0px"
                                        height="35px"
                                    />
                                </Box>
                            </Grid>
                            <Grid item md={12}>
                                <TableContainer sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 1 }}>
                                    <Table>
                                        <TableHead>
                                            {testType === Certificate.Transcripts ? (
                                                <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                                    <Cell width="25%">Grade</Cell>
                                                    <Cell width="25%">Upload Date</Cell>
                                                    <Cell width="25%">View Transcript</Cell>
                                                    {user?.user_type_id === UserTypeId.Student && (
                                                        <Cell>Action</Cell>)}
                                                </TableRow>
                                            ) : (
                                                <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                                    <Cell width="15%">Certificate</Cell>
                                                    <Cell width="15%">Issue Date</Cell>
                                                    <Cell width="15%">Valid Till</Cell>
                                                    <Cell width="30%">Description</Cell>
                                                    <Cell width="15%">View Transcript</Cell>
                                                    {user?.user_type_id === UserTypeId.Student && (
                                                        <Cell>Action</Cell>)}
                                                </TableRow>
                                            )}
                                        </TableHead>
                                        <TableBody>
                                            {testType === Certificate.Transcripts ? transcriptData?.data?.length > 0 ? transcriptData?.data?.map((course) => (
                                                <TableRow key={course.id}>
                                                    <InnerCell sx={{ display: 'flex', gap: 1, height: '45px', alignItems: 'center', ml: 2 }}>
                                                        {user?.student_current_grade === course.title && (
                                                            <Box sx={{ background: '#3AC968', borderRadius: '70%', width: '25px', height: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0.3 }}>
                                                                <Check sx={{ color: 'white', fontSize: 12 }} />
                                                            </Box>
                                                        )}
                                                        <Typography color={theme.palette.background.dark}>{DisplayGrade[course.title]}</Typography>
                                                    </InnerCell>
                                                    <InnerCell>
                                                        <Typography color={theme.palette.background.dark}>{moment(course.uploadDate).format('MMM DD, YYYY')}</Typography>
                                                    </InnerCell>
                                                    <InnerCell>
                                                        <Box display="flex" gap={1} alignItems="center" justifyContent="center"
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <a
                                                                href={`${process.env.REACT_APP_BASE_URL}/file?bname=${course.documents?.url}&download=true`}
                                                                download
                                                                target="_blank"
                                                                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                            >
                                                                <img src="/Images/pdficon.png" alt="pdf icon" width="30px" />
                                                                <Typography color={theme.palette.background.dark}>
                                                                    View PDF
                                                                </Typography>
                                                            </a>
                                                        </Box>
                                                    </InnerCell>
                                                    {user?.user_type_id === UserTypeId.Student && (
                                                        <InnerCell>
                                                            <Button
                                                                sx={{
                                                                    border: '1px solid #0000001A',
                                                                    height: '25px',
                                                                    color: 'black',
                                                                    fontSize: 14
                                                                }}
                                                                onClick={() => {
                                                                    setAddOpen(true);
                                                                    setFileNames(course.documents);
                                                                    setGrade(course.grade);
                                                                    setDocumentId(course.student_document_id);
                                                                }}
                                                            >
                                                                Upload
                                                            </Button>
                                                        </InnerCell>)}
                                                </TableRow>
                                            )) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">
                                                        <Typography>No Data Found</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                                : certificateData?.data?.length > 0 ? certificateData?.data?.map((course) => (
                                                    <TableRow key={course.id}>
                                                        <InnerCell>
                                                            <Typography color={theme.palette.background.dark}>{course.title}</Typography>
                                                        </InnerCell>
                                                        <InnerCell>
                                                            <Typography color={theme.palette.background.dark}>{moment(course.issue_date).format('MMM DD, YYYY')}</Typography>
                                                        </InnerCell>
                                                        <InnerCell>
                                                            <Typography color={theme.palette.background.dark}>{moment(course.valid_till).format('MMM DD, YYYY')}</Typography>
                                                        </InnerCell>
                                                        <InnerCell>
                                                            <Typography color={theme.palette.background.dark}>{course.description}</Typography>
                                                        </InnerCell>
                                                        <InnerCell>
                                                            <Box display="flex" gap={1} alignItems="center" justifyContent="center"
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                <a
                                                                    href={`${process.env.REACT_APP_BASE_URL}/file?bname=${course.documents?.url}&download=true`}
                                                                    download
                                                                    target="_blank"
                                                                    style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                                >
                                                                    <img src="/Images/pdficon.png" alt="pdf icon" width="30px" />
                                                                    <Typography color={theme.palette.background.dark}>
                                                                        View PDF
                                                                    </Typography>
                                                                </a>
                                                            </Box>
                                                        </InnerCell>
                                                        {user?.user_type_id === UserTypeId.Student && (
                                                            <InnerCell>
                                                                <IconButton size="small"
                                                                    onClick={() => {
                                                                        setDeleteConfirm(course);
                                                                    }}
                                                                >
                                                                    <DeleteForever sx={{ color: 'red', fontSize: 20 }} />
                                                                </IconButton>
                                                                <IconButton size="small"
                                                                    onClick={() => {
                                                                        setAddOpen(true);
                                                                        setFileNames(course.documents);
                                                                        setFormData({
                                                                            name: course.title,
                                                                            issue_date: course.issue_date ? moment(course.issue_date).format('YYYY-MM-DD') : '',
                                                                            valid_date: course.valid_till ? moment(course.valid_till).format('YYYY-MM-DD') : '',
                                                                            notes: course.description
                                                                        });
                                                                        setDocumentId(course.student_document_id);
                                                                    }}
                                                                >
                                                                    <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                                                </IconButton>
                                                            </InnerCell>)}
                                                    </TableRow>
                                                )) : (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center">
                                                            <Typography>No Data Found</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Box sx={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                                        component="div"
                                        count={transcriptData?.total || certificateData?.total || 0}
                                        rowsPerPage={pageSize}
                                        page={pageNo}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        sx={{
                                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                                fontSize: '0.875rem',
                                            },
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Fragment>
                }
            />
        </Fragment>
    )
}

export default Certificates;